import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Switch, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerAccount } from '@/api/axios/auth';
import { RegisterPayloadSchema, type RegisterPayload } from '@/types/api/payload/auth';

export function RegisterDialog() {
  const { t } = useTranslation();
  const { register, control, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<RegisterPayload>({
    resolver: zodResolver(RegisterPayloadSchema),
    defaultValues: { username: '', is_private: false },
  });

  const submit = handleSubmit(async (values) => {
    try {
      await registerAccount(values);
    } catch (error) {
      setError('root', { message: error instanceof Error ? error.message : t('registration.failed') });
    }
  });

  return (
    <Dialog open disableEscapeKeyDown fullWidth maxWidth="xs" aria-labelledby="register-title" aria-describedby="register-description">
      <form onSubmit={submit} noValidate>
        <DialogTitle id="register-title">{t('registration.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="register-description" sx={{ mb: 2 }}>
            {t('registration.description')}
          </DialogContentText>
          {errors.root && <Alert severity="error" sx={{ mb: 2 }}>{errors.root.message}</Alert>}
          <TextField
            {...register('username')}
            label={t('profile-editor.username')}
            autoComplete="username"
            autoFocus
            required
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.username)}
            helperText={errors.username?.message ? t(errors.username.message) : undefined}
          />
          <Controller name="is_private" control={control} render={({ field }) => (
            <FormControlLabel
              sx={{ m: 0, mt: 2, gap: 1.5, minHeight: 32, alignItems: 'center' }}
              label={t('profile-editor.private-account')}
              control={<Switch sx={{ flexShrink: 0 }} checked={field.value} onChange={(_, checked) => field.onChange(checked)} onBlur={field.onBlur} inputRef={field.ref} name={field.name} disabled={isSubmitting} />}
            />
          )} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting} fullWidth>
            {isSubmitting ? t('registration.creating') : t('registration.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
